import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {Toggle} from '@forecast-it/design-system';
import {createToast} from '../toasts/toast';
import {commitSetEnabled, type ScheduleSetSnapshot} from './scheduleMutations';
import {SCHEDULING_MESSAGE} from './schedulingMessages';
import {reportRefusal} from './ScheduleList';

const TOAST_DURATION_MS = 4000;

type ScheduleSetToggleProps = {
	set: ScheduleSetSnapshot;
	name: string;
	onReload?: () => void;
};

// Switching off stops every run of the set and changes no schedule. The toast is shown once the service
// has stored the switch; a refused switch returns the toggle to the stored state.
export const ScheduleSetToggle = ({set, name, onReload = () => undefined}: ScheduleSetToggleProps): JSX.Element => {
	const {formatMessage} = useIntl();
	// One write at a time, so a quick on, off, on cannot land out of order.
	const [isSaving, setIsSaving] = useState(false);
	const change = (enabled: boolean) => {
		setIsSaving(true);
		commitSetEnabled(set, enabled, {
			onSucceeded: () =>
				createToast({
					duration: TOAST_DURATION_MS,
					message: formatMessage({id: enabled ? SCHEDULING_MESSAGE.TOAST_ON : SCHEDULING_MESSAGE.TOAST_OFF}, {name}),
				}),
			onRefused: failure => reportRefusal(failure, formatMessage, onReload),
			onSettled: () => setIsSaving(false),
		});
	};
	return (
		<Toggle checked={set.enabled} onChange={change} disabled={isSaving} cy={'schedule-set-toggle'}>
			{formatMessage({id: set.enabled ? SCHEDULING_MESSAGE.TOGGLE_ON : SCHEDULING_MESSAGE.TOGGLE_OFF}, {name})}
		</Toggle>
	);
};
